'use client'
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import type { ReactNode, RefObject } from 'react'

interface VideoVisibilityContextType {
  observerRef: RefObject<IntersectionObserver>
  visibleVideos: Set<string>
  registerVideo: (videoId: string, element: HTMLVideoElement) => void
  unregisterVideo: (videoId: string, element: HTMLVideoElement) => void
}

const VideoVisibilityContext = createContext<VideoVisibilityContextType | null>(null)

interface VideoVisibilityProviderProps {
  children: ReactNode
  threshold?: number
  rootMargin?: string
}

export const VideoVisibilityProvider: React.FC<VideoVisibilityProviderProps> = ({
  children,
  threshold = 0.1,
  rootMargin = '40px'
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [visibleVideos, setVisibleVideos] = useState<Set<string>>(new Set())

  useEffect(() => {
    // Create the intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleVideos(prev => {
          const newSet = new Set(prev)

          entries.forEach(entry => {
            const videoId = entry.target.id
            if (entry.isIntersecting) {
              newSet.add(videoId)
            } else {
              newSet.delete(videoId)
            }
          })

          return newSet
        })
      },
      {
        threshold,
        rootMargin,
        // Use root: null for viewport detection
        root: null
      }
    )

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [threshold, rootMargin])

  const registerVideo = useCallback((videoId: string, element: HTMLVideoElement) => {
    if (!observerRef.current || !element) return

    // Set the video ID for tracking
    element.id = videoId

    // Start observing this video element
    observerRef.current.observe(element)
  }, [])

  const unregisterVideo = useCallback((videoId: string, element: HTMLVideoElement) => {
    if (!observerRef.current || !element) return

    // Stop observing this video element
    observerRef.current.unobserve(element)

    // Remove from visible videos set
    setVisibleVideos(prev => {
      const newSet = new Set(prev)
      newSet.delete(videoId)
      return newSet
    })
  }, [])

  const contextValue: VideoVisibilityContextType = {
    observerRef,
    visibleVideos,
    registerVideo,
    unregisterVideo
  }

  return (
    <VideoVisibilityContext.Provider value={contextValue}>
      {children}
    </VideoVisibilityContext.Provider>
  )
}

export const useVideoVisibilityContext = (): VideoVisibilityContextType => {
  const context = useContext(VideoVisibilityContext)
  if (!context) {
    throw new Error('useVideoVisibilityContext must be used within a VideoVisibilityProvider')
  }
  return context
}
